import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'novix.theme';

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles system -> light -> dark -> system, for the header toggle. */
  cycle: () => void;
}

/**
 * The initial preference is LIGHT, not `system`.
 *
 * The approved mockup is a light interface. Defaulting to `system` meant every
 * visitor on a dark-mode phone or laptop — which is most of them — saw a screen
 * the owner never approved, on first paint, before touching anything. Dark is
 * still fully supported and still overrides the OS in both directions; it is
 * simply opt-in now rather than inherited.
 */
export const DEFAULT_THEME: ThemePreference = 'light';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_THEME;
}

/**
 * Writes `data-theme` on <html>. `system` removes the attribute so the tokens'
 * `color-scheme: light dark` falls back to `prefers-color-scheme`; an explicit
 * value overrides the OS in BOTH directions, which is the point.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);

  try {
    // `system` is PERSISTED, not cleared. Now that the default is `light`,
    // clearing storage would mean "follow the OS" silently reverts to light on
    // the next reload — the user's choice has to survive.
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* storage unavailable — theme still applies for this session */
  }
}

// Starts at the default so the first press of the toggle moves somewhere the
// user can see, rather than into `system` (which on a light OS looks identical).
const ORDER: ThemePreference[] = ['light', 'dark', 'system'];

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: readStored(),
  setPreference: (preference) => {
    applyTheme(preference);
    set({ preference });
  },
  cycle: () => {
    const current = get().preference;
    const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length] ?? 'system';
    get().setPreference(next);
  }
}));

/** Resolves what the user will actually see right now. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
